import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ToastService } from '@irembo-andela/irembogov3-common';
import { BehaviorSubject, finalize } from 'rxjs';
import { EntityWithSubscriptions } from '../../../../../core/classes/entity-with-subscriptions';
import {
  Notification,
  NotificationPreviewData,
  NotificationTemplate,
  NotificationType,
} from '../../../../../core/models/notification.model';
import { IFieldFormGroup } from '../../../../../core/models/service-change-requests-form-fields.model';
import { IServiceLanguage } from '../../../../../core/models/service-language.model';
import { WorkflowState } from '../../../../../core/models/workflow.model';
import { ServiceChangeRequestService } from '../../../../../core/services/service-change-request.service';
import './quill/form-fields.module';
import './quill/custom-button.module';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ChooseNotifificationTemplateComponent } from './choose-notifification-template/choose-notifification-template.component';

interface ITabButton {
  label: string;
  id: string;
  icon: string;
}

interface FormFieldItem {
  label: string;
  value: string;
}

interface QuillConfig {
  toolbar: string[][];
  formFields: {
    items: FormFieldItem[];
  };
  customButton?: {
    label: string;
    callback: () => void;
  };
}

const EMAIL_TAB: ITabButton = {
  label: 'Email',
  id: 'email-tab',
  icon: 'fa-solid fa-envelope',
};

const SMS_TAB: ITabButton = {
  label: 'SMS',
  id: 'sms-tab',
  icon: 'fas fa-mobile',
};

@Component({
  selector: 'irembogov-notification-configuration-form',
  templateUrl: './notification-configuration-form.component.html',
  styleUrls: ['./notification-configuration-form.component.scss'],
})
export class NotificationConfigurationFormComponent
  extends EntityWithSubscriptions
  implements OnInit, OnChanges, AfterViewInit
{
  @Input() workflowState!: WorkflowState;
  @Input() currentNotification!: Notification | null;
  @Input() formFields!: any;
  @Output() notificationChange = new EventEmitter<Notification>();
  @Output() cancel = new EventEmitter<void>();

  tabButtons: ITabButton[] = [];
  selectedTab: ITabButton = {
    label: 'Email',
    id: 'email-tab',
    icon: 'fa-solid fa-envelope',
  };
  quillConfig: QuillConfig = {
    toolbar: [['bold', 'italic', 'underline'], ['link']],
    customButton: {
      label: 'Choose Template',
      callback: () => {
        this.ngZone.run(() => {
          this.openChooseTemplateModal();
        });
      },
    },
    formFields: {
      items: [
        { label: 'Application Number', value: '${applicationNumber!}' },
        { label: 'Applicant Name', value: '${applicantName!}' },
        { label: 'Service Name', value: '${serviceName!}' },
        { label: 'Price', value: '${formattedPrice!}' },
        { label: 'Currency', value: '${currency!}' },
        { label: 'Bill ID', value: '${billReferenceNumber!}' },
        { label: 'Payment Expiry Date', value: '${paymentExpiration!}' },
      ],
    },
  };
  languages: IServiceLanguage[] = [];
  selectedLanguage: IServiceLanguage | null = null;
  languageForms: { [key: string]: FormGroup } = {};
  NotificationType = NotificationType;
  notificationPeviewData: NotificationPreviewData = {
    'en-US': {
      title: '',
      message: '',
    },
  };
  templates: NotificationTemplate[] = [];
  loadingTemplates = false;
  selectedTemplate$ = new BehaviorSubject<NotificationTemplate | undefined>(
    undefined
  );
  emailSwitch = false;
  smsSwitch = false;
  editEmailTemplateSwitch = false;
  editSMSTemplateSwitch = false;
  isEmailTemplateSelected = false;
  isSmsTemplateSelected = false;
  editorsTabs: ITabButton[] = [
    {
      label: 'Text Editor',
      id: 'text-editor-tab',
      icon: '',
    },
    {
      label: 'Code Editor',
      id: 'code-editor-tab',
      icon: '',
    },
  ];
  selectedEditorTab: ITabButton = this.editorsTabs[0];
  htmlEdiorOptions = {
    theme: 'vs-dark',
    language: 'html',
    automaticLayout: true,
    fontSize: 12,
    wordWrap: 'on',
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    formatOnPaste: true,
    formatOnType: true,
    folding: true,
    bracketPairColorization: { enabled: true },
    autoClosingTags: true,
  };

  private previousWorkflowState = '';

  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private modalService: NgbModal,
    private ngZone: NgZone
  ) {
    super();
  }

  ngOnInit(): void {
    this.getNotificationTemplates();

    this.selectedTemplate$.subscribe(() => {
      const locale = this.selectedLanguage?.locale || '';

      if (this.languageForms[locale]) {
        const formValues = this.languageForms[locale].value;

        this.notificationPeviewData = {
          ...this.notificationPeviewData,
          [locale]: {
            title: formValues.title,
            message: this.isEmailTabSelected()
              ? formValues.emailMessage
              : formValues.smsBody,
          },
        };
      }
    });

    this.subscriptions.push(
      this.serviceChangeRequestService.languages.subscribe(response => {
        this.languages = response.filter(
          language => language.status === 'ACTIVE'
        );
        this.selectedLanguage = this.languages[0] || null;
        this.initForm();
      })
    );

    if (this.currentNotification) {
      this.populateForm(this.currentNotification);
    }

    this.updateNotificationPreviewData();

    const items: FormFieldItem[] = [];

    this.formFields?.fields?.forEach((field: any) => {
      field?.fieldGroup.forEach((fieldGroup: any) => {
        fieldGroup.fieldGroup.forEach((fg: any) => {
          fg.fieldGroup.forEach((element: IFieldFormGroup) => {
            if (
              element?.type === 'customcascadingdropdowns' &&
              element.props?.configs
            ) {
              element.props.configs.forEach((config: any) => {
                config['type'] = 'cascading_customdropdown';
                items.push({
                  label: config.label,
                  value: '${' + config.key + '!}',
                });
              });
              return;
            }
            if (element?.type !== 'customfileupload') {
              items.push({
                label: element.props.label,
                value: '${' + element.key + '!}',
              });
            }
          });
        });
      });
    });

    this.quillConfig.formFields.items = [
      ...this.quillConfig.formFields.items,
      ...items,
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['workflowState']) {
      const newStateKey = this.getWorkflowStateKey(
        changes['workflowState'].currentValue
      );
      const stateChanged = newStateKey !== this.previousWorkflowState;

      if (stateChanged) {
        this.previousWorkflowState = newStateKey;

        if (
          this.currentNotification &&
          this.isSameState(
            this.currentNotification.workflowContext,
            this.workflowState
          )
        ) {
          this.populateForm(this.currentNotification);
        } else {
          this.resetForm();
        }
      }
    }

    if (changes['currentNotification']) {
      const newNotification = changes['currentNotification'].currentValue;
      if (
        newNotification &&
        this.isSameState(newNotification.workflowContext, this.workflowState)
      ) {
        this.populateForm(newNotification);
      }
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const currentForm =
        this.languageForms[this.selectedLanguage?.locale || ''];
      const htmlCodeControl = currentForm.get('htmlCode');

      if (htmlCodeControl) {
        this.subscriptions.push(
          htmlCodeControl.valueChanges.subscribe(value => {
            if (this.isCodeEditorSelected()) {
              const newTemplate: NotificationTemplate = {
                id: '',
                template: value,
                templateName: '',
                type: 'EMAIL',
                subject: value.title,
                language: this.selectedLanguage?.locale || '',
              };
              this.selectedTemplate$.next(newTemplate);
            }
          })
        );
      }
    }, 100);
  }

  updateNotificationPreviewData() {
    this.subscriptions.push(
      this.languageForms[
        this.selectedLanguage?.locale || ''
      ].valueChanges.subscribe(value => {
        if (this.selectedLanguage) {
          const previewData = {
            title: value.title,
            message: this.isEmailTabSelected()
              ? value.emailMessage
              : value.smsBody,
          };
          this.notificationPeviewData = {
            ...this.notificationPeviewData,
            [this.selectedLanguage.locale]: previewData,
          };
        }
      })
    );
  }

  private initForm(): void {
    this.languages.forEach(language => {
      this.notificationPeviewData[language.locale] = {
        title: '',
        message: '',
      };

      this.languageForms[language.locale] = this.formBuilder.group({
        title: ['', []],
        emailMessage: ['', []],
        smsBody: ['', []],
        htmlCode: ['', []],
        emailTemplateName: ['', []],
        smsTemplateName: ['', []],
      });
    });
  }

  isEmailTabSelected(): boolean {
    return this.selectedTab.id === 'email-tab';
  }

  onTabChange(tab: ITabButton) {
    this.selectedTab = tab;
    const formValues =
      this.languageForms[this.selectedLanguage?.locale || ''].value;
    this.notificationPeviewData = {
      ...this.notificationPeviewData,
      [this.selectedLanguage?.locale || '']: {
        title: formValues.title,
        message: this.isEmailTabSelected()
          ? formValues.emailMessage
          : formValues.smsBody,
      },
    };
  }

  onLanguageChange(language: IServiceLanguage) {
    this.selectedLanguage = language;
    this.selectedTab = this.tabButtons[0];
    this.updateNotificationPreviewData();

    if (this.currentNotification) {
      this.selectedTemplate$.next(
        this.templates.find(
          template =>
            template.language === language.locale &&
            (this.isEmailTabSelected()
              ? template.type === 'EMAIL'
              : template.type === 'SMS')
        )
      );
    }
  }

  getFormGroup(language: IServiceLanguage): FormGroup {
    return this.languageForms[language.locale];
  }

  onSubmit() {
    const isDynamicNotificationType =
      this.isEmailTemplateSelected &&
      this.isSmsTemplateSelected &&
      !this.editEmailTemplateSwitch &&
      !this.editSMSTemplateSwitch;

    const notification: Notification = {
      workflowContext: this.workflowState,
      type: isDynamicNotificationType
        ? NotificationType.NOTIFICATION_ENGINE_TEMPLATE
        : NotificationType.STATIC,
      staticTemplate: isDynamicNotificationType ? null : {},
      dynamicTemplate: isDynamicNotificationType ? {} : null,
    };

    for (const language of this.languages) {
      const formGroup = this.languageForms[language.locale];
      if (isDynamicNotificationType) {
        notification.dynamicTemplate = notification.dynamicTemplate || {};

        if (formGroup.get('emailTemplateName')?.value) {
          notification.dynamicTemplate[language.locale] = {
            emailTemplateName: formGroup.get('emailTemplateName')?.value,
          };
        }

        if (formGroup.get('smsTemplateName')?.value) {
          notification.dynamicTemplate[language.locale] = {
            ...notification.dynamicTemplate[language.locale],
            smsTemplateName: formGroup.get('smsTemplateName')?.value,
          };
        }
      } else {
        const template =
          (this.selectedTemplate$.getValue() as NotificationTemplate) ||
          this.templates.find(
            template =>
              template.language === language.locale && template.type === 'EMAIL'
          );

        notification.staticTemplate = notification.staticTemplate || {};

        const emailBody = this.isCodeEditorSelected()
          ? formGroup.get('htmlCode')?.value
          : this.updateEmailBody(
              template,
              formGroup.get('title')?.value,
              formGroup.get('emailMessage')?.value
            );

        if (formGroup.get('title')?.value && emailBody) {
          notification.staticTemplate[language.locale] = {
            ...notification.staticTemplate[language.locale],
            title: formGroup.get('title')?.value,
            subject: formGroup.get('title')?.value,
            emailBody,
            emailMessage: formGroup.get('emailMessage')?.value,
          };
        }

        if (formGroup.get('smsBody')?.value) {
          notification.staticTemplate[language.locale] = {
            ...notification.staticTemplate[language.locale],
            smsBody: formGroup.get('smsBody')?.value,
          };
        }
      }
    }

    if (notification.staticTemplate || notification.dynamicTemplate) {
      this.notificationChange.emit(notification);
    }
  }

  isFormValid(): boolean {
    const form = this.languageForms[this.selectedLanguage?.locale || ''];
    if (this.emailSwitch && !this.smsSwitch) {
      if (form && form.get('emailMessage')?.value && form.get('title')?.value) {
        return true;
      }

      return false;
    }

    if (this.smsSwitch && !this.emailSwitch) {
      if (form && form.get('smsBody')?.value) {
        return true;
      }

      return false;
    }

    if (this.emailSwitch && this.smsSwitch) {
      if (
        form &&
        form.get('emailMessage')?.value &&
        form.get('smsBody')?.value &&
        form.get('title')?.value
      ) {
        return true;
      }

      return false;
    }

    return false;
  }

  private populateForm(notification: Notification) {
    if (notification.type === NotificationType.STATIC) {
      Object.keys(notification.staticTemplate || {}).forEach(locale => {
        const values = notification.staticTemplate?.[locale];
        if (this.languageForms[locale]) {
          this.languageForms[locale].patchValue({
            title: values?.title || values?.subject,
            emailMessage: values?.emailMessage,
            smsBody: values?.smsBody,
          });
          this.languageForms[locale].markAsPristine();

          const emailTemplate: NotificationTemplate = {
            id: '',
            type: 'EMAIL',
            subject: values?.subject || '',
            language: locale,
            templateName: '',
            template: values?.emailBody || '',
          };

          const smsTemplate: NotificationTemplate = {
            id: '',
            type: 'SMS',
            subject: values?.subject || '',
            language: locale,
            templateName: '',
            template: values?.smsBody || '',
          };

          this.templates.push(emailTemplate, smsTemplate);
        }
      });

      this.selectedTemplate$.next(
        this.templates.find(
          template =>
            template.language === this.selectedLanguage?.locale &&
            (this.isEmailTabSelected()
              ? template.type === 'EMAIL'
              : template.type === 'SMS')
        )
      );

      const staticTemplate = notification.staticTemplate || {};

      this.emailSwitch = Object.values(staticTemplate).some(
        values => !!values?.emailBody || !!values?.emailMessage
      );
      this.smsSwitch = Object.values(staticTemplate).some(
        values => !!values?.smsBody
      );

      if (
        this.emailSwitch &&
        !this.tabButtons.some(button => button.id === EMAIL_TAB.id)
      ) {
        this.tabButtons.push(EMAIL_TAB);
      }
      if (
        this.smsSwitch &&
        !this.tabButtons.some(button => button.id === SMS_TAB.id)
      ) {
        this.tabButtons.push(SMS_TAB);
      }
      if (this.tabButtons.length > 0) {
        this.selectedTab = this.tabButtons[0];
      }
    }

    if (notification.type === NotificationType.NOTIFICATION_ENGINE_TEMPLATE) {
      Object.keys(notification.dynamicTemplate || {}).forEach(locale => {
        const values = notification.dynamicTemplate?.[locale];
        this.languageForms[locale].patchValue({
          emailTemplateName: values?.emailTemplateName,
          smsTemplateName: values?.smsTemplateName,
        });
        this.languageForms[locale].markAsPristine();
      });
    }
  }

  private getWorkflowStateKey(state: WorkflowState): string {
    return `${state.startState}-${state.event}-${state.endStateType}`;
  }

  private isSameState(state1: WorkflowState, state2: WorkflowState): boolean {
    return (
      state1.startState === state2.startState &&
      state1.event === state2.event &&
      state1.endStateType === state2.endStateType
    );
  }

  private resetForm() {
    this.languages.forEach(language => {
      this.languageForms[language.locale].reset();
      this.languageForms[language.locale].markAsPristine();
    });
  }

  getNotificationTemplates() {
    this.loadingTemplates = true;
    const queryParams = 'templateName=SERVICE_STATIC_NOTIFICATION_TEMPLATE';

    this.serviceChangeRequestService
      .getNotificationTemplates(queryParams)
      .pipe(finalize(() => (this.loadingTemplates = false)))
      .subscribe({
        next: response => {
          this.templates = response.data;
          const template = this.templates.find(
            template => template.language === this.selectedLanguage?.locale
          );

          if (template && !this.selectedTemplate$.getValue()) {
            this.selectedTemplate$.next(template);
          }
        },
        error: error => {
          this.toastService.show({
            type: 'error',
            body: error.error.message,
          });
        },
      });
  }

  updateEmailBody(
    template: NotificationTemplate,
    title: string,
    emailMessage: string
  ) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(template.template, 'text/html');
    const body = doc.body;
    if (body) {
      let contentText = body.innerHTML;
      contentText = contentText.replace('${message}', emailMessage);
      contentText = contentText.replace('${title}', title);
      contentText = contentText.replace(
        '${year}',
        new Date().getFullYear().toString()
      );

      doc.body.innerHTML = contentText;
    }

    return doc.documentElement.outerHTML;
  }

  onCancel() {
    this.cancel.emit();
  }

  toggleEmailSwitch(event: any) {
    this.emailSwitch = event.target.checked;
    if (this.emailSwitch) {
      this.tabButtons = [EMAIL_TAB, ...this.tabButtons];
    } else {
      this.tabButtons = this.tabButtons.filter(
        button => button.id !== EMAIL_TAB.id
      );
    }

    if (this.tabButtons.length > 0) {
      this.selectedTab = this.tabButtons[0];
    }
  }

  toggleSmsSwitch(event: any) {
    this.smsSwitch = event.target.checked;
    if (this.smsSwitch) {
      this.tabButtons = [...this.tabButtons, SMS_TAB];
    } else {
      this.tabButtons = this.tabButtons.filter(
        button => button.id !== SMS_TAB.id
      );
    }

    if (this.tabButtons.length > 0) {
      this.selectedTab = this.tabButtons[0];
    }
  }

  toggleEditEmailTemplateSwitch(event: any) {
    this.editEmailTemplateSwitch = event.target.checked;
  }

  toggleEditSMSTemplateSwitch(event: any) {
    this.editSMSTemplateSwitch = event.target.checked;
  }

  onEditorTabChange(tab: ITabButton) {
    this.selectedEditorTab = tab;

    if (this.selectedEditorTab.id === 'code-editor-tab') {
      let emailBody;

      if (this.selectedTemplate$.getValue()) {
        emailBody = this.selectedTemplate$.getValue()?.template;
      } else {
        const values =
          this.currentNotification?.staticTemplate?.[
            this.selectedLanguage?.locale || ''
          ];

        emailBody = values?.emailBody || '';
      }

      this.languageForms[this.selectedLanguage?.locale || ''].patchValue({
        htmlCode: this.getOuterHtml(emailBody || ''),
      });
    } else {
      const values =
        this.currentNotification?.staticTemplate?.[
          this.selectedLanguage?.locale || ''
        ];

      let emailMessage;

      if (values) {
        emailMessage = values.emailMessage;
      } else {
        emailMessage = this.selectedTemplate$.getValue()?.template || '';
      }

      this.languageForms[this.selectedLanguage?.locale || ''].patchValue({
        emailMessage,
      });

      const template = this.templates.find(
        template => template.language === this.selectedLanguage?.locale
      );

      if (template) {
        this.selectedTemplate$.next(template);
      }
    }
  }

  isTextEditorSelected(): boolean {
    return this.selectedEditorTab.id === 'text-editor-tab';
  }

  isCodeEditorSelected(): boolean {
    return this.selectedEditorTab.id === 'code-editor-tab';
  }

  openChooseTemplateModal() {
    const modalRef = this.modalService.open(
      ChooseNotifificationTemplateComponent,
      {
        centered: true,
        windowClass: 'choose-template-modal',
        fullscreen: true,
      }
    );

    modalRef.componentInstance.isEmail = this.isEmailTabSelected();

    modalRef.result
      .then(result => {
        if (result && result.length && result.length > 0) {
          const templateName = result[0].templateName;
          if (this.isEmailTabSelected()) {
            const emailTemplates = result.filter(
              (t: any) => t.type === 'EMAIL'
            );
            this.selectedTemplate$.next(
              emailTemplates.find(
                (t: any) => t.language === this.selectedLanguage?.locale
              )
            );
            this.isEmailTemplateSelected = true;

            this.languages.forEach((language: IServiceLanguage) => {
              this.languageForms[language.locale].patchValue({
                emailTemplateName: templateName,
              });
            });
          } else {
            const smsTemplates = result.filter((t: any) => t.type === 'SMS');

            smsTemplates.forEach((template: any) => {
              const locale = template.language;

              const language = this.languages.find(
                lang => lang.locale === locale
              );
              if (language) {
                this.languageForms[language.locale].patchValue({
                  smsBody: template.template,
                });
              }
            });
            this.isSmsTemplateSelected = true;

            this.languages.forEach((language: IServiceLanguage) => {
              this.languageForms[language.locale].patchValue({
                smsTemplateName: templateName,
              });
            });
          }

          this.templates.push(result);
        }
      })
      .catch(() => {
        console.log('Modal dismissed without selection');
      });
  }

  getOuterHtml(body: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(body, 'text/html');

    return doc.documentElement.outerHTML;
  }
}
